import {
  type ReceiptDetail,
  type ReceiptDetailParcel,
  type ReceiptFeeAmount,
  type ReceiptFeedbackDetails,
  type ReceiptParcel,
  type ReceiptUser,
} from './aliases';
import {
  isManualParcel,
  isShippingLabelExpired,
  mapDepopStatus,
  mapManualStatus,
  mapPresentationAddress,
} from './presentationAdapter';
import {
  type PaymentProvider,
  PayoutDescriptionStatus,
  type Receipt,
  ReceiptStatus,
  Role,
  ShippingStatus,
} from './types';

import { type CurrencyCode } from '@/modules/currencies/constants';
import { type Feedback } from '@/modules/feedback/types';
import { type Parcel } from '@/modules/parcels/types';

function mapUser(user: ReceiptUser): Receipt.User {
  const pictureUrl = user.picture_data
    ? Object.values(user.picture_data.formats)[0]?.url
    : undefined;
  return {
    id: user.id,
    username: user.username,
    ...(user.first_name ? { firstName: user.first_name } : {}),
    ...(user.last_name ? { lastName: user.last_name } : {}),
    ...(pictureUrl ? { pictureUrl } : {}),
    isActive: user.is_active,
  };
}

function mapFeeAmount(
  fee: ReceiptFeeAmount | undefined
): Receipt.FeeAmount | undefined {
  if (!fee) {
    return undefined;
  }
  return {
    amount: fee.amount,
    ...(fee.original_amount ? { originalAmount: fee.original_amount } : {}),
  };
}

function isManualDetailParcel(parcel: ReceiptDetailParcel): boolean {
  return isManualParcel({
    shipping_provider: parcel.shipping_provider,
  } as ReceiptParcel);
}

function isLabelExpired(parcel: ReceiptDetailParcel): boolean {
  return isShippingLabelExpired(parcel.state, parcel.label?.expires_at);
}

function deriveParcelDisplayStatus(parcel: ReceiptDetailParcel): string {
  if (isManualDetailParcel(parcel)) {
    return mapManualStatus(parcel.state);
  }
  if (isLabelExpired(parcel)) {
    return mapDepopStatus('SHIPPING_LABEL_EXPIRED');
  }
  return mapDepopStatus(parcel.state);
}

function mapReceiptStatus(
  status: ReceiptDetail['status'],
  parcels: ReceiptDetailParcel[]
): ReceiptStatus {
  if (status === 'cancelled') {
    return ReceiptStatus.Cancelled;
  }

  const [firstParcel] = parcels;
  if (firstParcel) {
    return deriveParcelDisplayStatus(firstParcel) as ReceiptStatus;
  }

  switch (status) {
    case 'shipping_pending':
      return ReceiptStatus.AwaitingShipping;
    case 'shipped':
      return ReceiptStatus.Shipped;
    case 'delivered':
      return ReceiptStatus.Delivered;
    default:
      return ReceiptStatus.Unknown;
  }
}

/** presentation detail parcel → the web detail `Parcel.AnyParcel` (payer + provider extras). */
export function mapPresentationDetailParcel(
  parcel: ReceiptDetailParcel
): Parcel.AnyParcel {
  const payer = parcel.payer === 'seller' ? Role.Seller : Role.Buyer;
  const base = {
    parcelId: parcel.parcel_id,
    shippingProvider: parcel.shipping_provider,
    payer,
    purchaseLineItems: parcel.line_ids,
    ...(parcel.shipping_deadline
      ? { shippingDeadline: parcel.shipping_deadline }
      : {}),
  };

  if (isManualDetailParcel(parcel)) {
    return {
      ...base,
      parcelStatus: mapManualStatus(parcel.state),
      ...(parcel.shipped_at ? { shippedAt: parcel.shipped_at } : {}),
      ...(parcel.shipping_tracking_number
        ? { shippingTrackingNumber: parcel.shipping_tracking_number }
        : {}),
      ...(parcel.shipping_carrier_name
        ? { shippingCarrierName: parcel.shipping_carrier_name }
        : {}),
    };
  }

  const courierAddress = parcel.courier_collection
    ? mapPresentationAddress(parcel.courier_collection.address)
    : undefined;

  return {
    ...base,
    parcelStatus: isLabelExpired(parcel)
      ? mapDepopStatus('SHIPPING_LABEL_EXPIRED')
      : mapDepopStatus(parcel.state),
    providerName:
      parcel.tracking?.provider_name ??
      parcel.depop_shipping_provider?.provider_name ??
      '',
    ...(parcel.tracking?.reference
      ? {
          tracking: {
            reference: parcel.tracking.reference,
            url: parcel.tracking.url ?? '',
          },
        }
      : {}),
    ...(parcel.shipment_timestamp
      ? { shipmentTimestamp: parcel.shipment_timestamp }
      : {}),
    ...(parcel.delivery_timestamp
      ? { deliveryTimestamp: parcel.delivery_timestamp }
      : {}),
    ...(parcel.depop_shipping_provider
      ? {
          depopShippingProvider: {
            providerId: parcel.depop_shipping_provider.provider_id,
            providerName: parcel.depop_shipping_provider.provider_name,
            ...(parcel.courier_collection && courierAddress
              ? {
                  courierCollection: {
                    address: courierAddress,
                    ...(parcel.courier_collection.collection_date
                      ? {
                          collectionDate:
                            parcel.courier_collection.collection_date,
                        }
                      : {}),
                  },
                }
              : {
                  dropOffLocatorUrl:
                    parcel.depop_shipping_provider.drop_off_locator_url,
                }),
          },
        }
      : {}),
  } as Parcel.AnyParcel;
}

function mapFeedbackEntry(
  entry: ReceiptFeedbackDetails | undefined,
  receipt: ReceiptDetail
): Feedback | undefined {
  if (!entry) {
    return undefined;
  }
  return {
    feedbackId: entry.id,
    buyerId: receipt.buyer.id,
    sellerId: receipt.seller.id,
    productId: entry.product_id,
    transactionId: receipt.transaction_id,
    rating: entry.rating,
    ...(entry.body ? { text: entry.body } : {}),
    dateCreated: entry.created_timestamp,
    unread: entry.is_unread,
    deleted: false,
  };
}

const PAYOUT_STATUS: Record<string, PayoutDescriptionStatus> = {
  paid: PayoutDescriptionStatus.Paid,
  pending: PayoutDescriptionStatus.Pending,
  in_transit: PayoutDescriptionStatus.InTransit,
  canceled: PayoutDescriptionStatus.Cancelled,
  cancelled: PayoutDescriptionStatus.Cancelled,
  failed: PayoutDescriptionStatus.Failed,
};

function mapPaymentDetails(receipt: ReceiptDetail): Receipt.PaymentDetails {
  const feeBreakdown = receipt.fee_breakdown;
  const sellerFee =
    mapFeeAmount(feeBreakdown?.seller_fee) ??
    (receipt.fee !== undefined ? { amount: receipt.fee } : { amount: '0.00' });
  const buyerFee = mapFeeAmount(feeBreakdown?.buyer_fee) ?? { amount: '0.00' };

  const { payout } = receipt;

  return {
    currency: receipt.currency as CurrencyCode,
    itemsAmount: receipt.items_amount,
    ...(receipt.undiscounted_items_amount
      ? { undiscountedItemsAmount: receipt.undiscounted_items_amount }
      : {}),
    ...(receipt.payment_fee ? { paymentFee: receipt.payment_fee } : {}),
    ...(receipt.fee ? { depopFee: receipt.fee } : {}),
    ...(mapFeeAmount(feeBreakdown?.boosting_fee)
      ? { boostingFee: mapFeeAmount(feeBreakdown?.boosting_fee) }
      : {}),
    buyerPaidAmount: receipt.buyer_amount,
    sellerReceivedAmount: receipt.seller_amount,
    ...(payout
      ? {
          payoutDescription: {
            id: payout.id,
            status:
              PAYOUT_STATUS[payout.status?.toLowerCase()] ??
              PayoutDescriptionStatus.Pending,
            arrivalDate: payout.arrival_date,
            amount: payout.amount,
            currency: payout.currency as CurrencyCode,
          },
        }
      : {}),
    ...(receipt.payout_estimated_date
      ? { payoutEstimatedDate: receipt.payout_estimated_date }
      : {}),
    buyerPaymentProvider: receipt.payment_system as PaymentProvider,
    shippingAmount: receipt.shipping_details?.shipping_amount ?? '0.00',
    buyerShippingAmount:
      receipt.shipping_details?.buyer_shipping_amount ?? '0.00',
    sellerShippingAmount:
      receipt.shipping_details?.seller_shipping_amount ?? '0.00',
    ...(receipt.tax_summary
      ? {
          taxSummary: {
            totalTaxAmount: receipt.tax_summary.total_tax_amount,
            shipToLocation: receipt.tax_summary.ship_to_location,
          },
        }
      : {}),
    buyerFee,
    sellerFee,
  };
}

/** The 2-value shipping axis on ReceiptBase (same partition as `getHighLevelShippingStatus`). */
const SHIPPED_STATUSES = new Set<ReceiptStatus>([
  ReceiptStatus.Shipped,
  ReceiptStatus.Returned,
  ReceiptStatus.Failed,
  ReceiptStatus.Delivered,
  ReceiptStatus.InTransit,
]);

export function mapPresentationReceiptDetail(
  receipt: ReceiptDetail
): Receipt.Receipt {
  const parcels = receipt.shipping_details?.parcels ?? [];
  const receiptStatus = mapReceiptStatus(receipt.status, parcels);

  return {
    purchaseId: receipt.purchase_id,
    transactionId: receipt.transaction_id,
    buyer: mapUser(receipt.buyer),
    seller: mapUser(receipt.seller),
    receiptStatus,
    shippingStatus: SHIPPED_STATUSES.has(receiptStatus)
      ? ShippingStatus.Shipped
      : ShippingStatus.AwaitingShipping,
    soldTimestamp: receipt.sold_timestamp,
    shippingAddress: {
      ...(mapPresentationAddress(receipt.shipping_details?.ship_to) ?? {
        name: '',
        address: '',
        city: '',
        postcode: '',
        country: '',
      }),
      ...(receipt.shipping_details?.ship_to?.email
        ? { email: receipt.shipping_details.ship_to.email }
        : {}),
    },
    ...(receipt.ship_from_address_id !== undefined
      ? { shipFromAddressId: receipt.ship_from_address_id }
      : {}),
    ...(receipt.cancellation_details
      ? { cancellationDetails: receipt.cancellation_details }
      : {}),
    refundDetails: {
      isRefundable: receipt.is_refundable,
      ...(receipt.refund_details
        ? {
            refundDate: receipt.refund_details.refund_date,
            buyerRefundAmount: receipt.refund_details.refund_amount,
          }
        : {}),
    },
    items: receipt.lines.map((line) => ({
      pictureUrl: line.picture_urls[0] ?? '',
      productId: line.product_id,
      description: line.description,
      ...(line.slug ? { slug: line.slug } : {}),
      ...(line.brand ? { brand: line.brand } : {}),
      itemAmount: line.product_amount ?? line.original_price,
      ...(line.discount
        ? { undiscountedAmount: line.discount.undiscounted_price_amount }
        : {}),
      itemCurrency: line.price_currency as CurrencyCode,
      ...(line.size ? { size: line.size } : {}),
      hasDepopShipping: line.has_depop_shipping,
      ...(line.sku ? { sku: line.sku } : {}),
      ...(mapFeeAmount(line.fee_breakdown?.boosting_fee)
        ? { boostingFee: mapFeeAmount(line.fee_breakdown?.boosting_fee) }
        : {}),
    })),
    parcels: parcels.map(mapPresentationDetailParcel),
    feedback: {
      needed: receipt.feedback.needed,
      ...(mapFeedbackEntry(receipt.feedback.buyer, receipt)
        ? { leftByBuyer: mapFeedbackEntry(receipt.feedback.buyer, receipt) }
        : {}),
      ...(mapFeedbackEntry(receipt.feedback.seller, receipt)
        ? { leftBySeller: mapFeedbackEntry(receipt.feedback.seller, receipt) }
        : {}),
    },
    paymentDetails: mapPaymentDetails(receipt),
    isBundle: receipt.is_bundle,
    ...(receipt.shipping_details?.requested_shipping_deadline_state
      ? {
          requestedShippingDeadlineState:
            receipt.shipping_details.requested_shipping_deadline_state,
        }
      : {}),
    ...(receipt.repop_line_items
      ? {
          lineItems: receipt.repop_line_items.map((line) => ({
            id: line.id,
            repopAvailable: line.repop_available,
          })),
        }
      : {}),
  };
}
